import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import SentimentMeter from "../components/SentimentMeter";
import { actionStyle, gateColor, stateColor, confColor, fmt, fmtTime } from "../lib/signals";
import { ArrowLeft, Warning, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiErr } from "../lib/api";

const GATE_ROWS = [
  ["Data Quality", "data_quality"], ["Market Gate", "market_gate"], ["Market Regime", "market_regime"],
  ["Sector", "sector_leadership"], ["RS Score", "sector_rs_score"], ["Smart Money", "smart_money"],
  ["Reliability", "reliability"], ["Liquidity", "liquidity"],
];

export default function SignalDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get(`/hotlist/${id}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!d) return <div className="text-muted-foreground py-10">Loading…</div>;
  const lv = d.levels || {};
  const isShort = d.action === "SHORT" || d.action === "SELL";

  const addToPortfolio = async () => {
    try {
      await api.post("/portfolio", { ticker: d.ticker, exchange: d.exchange, asset_type: d.asset_type, yahoo_symbol: d.yahoo_symbol });
      toast.success(`${d.ticker} added to portfolio watch`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-foreground/5 transition-colors duration-200"><ArrowLeft size={18} weight="bold" /></button>
          <div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight">{d.ticker} <span className="text-muted-foreground text-base font-medium">{d.exchange}</span></h1>
            <p className="text-xs text-muted-foreground">{d.yahoo_symbol} · {d.decision_reason}</p>
          </div>
        </div>
        <button data-testid="add-portfolio-btn" onClick={addToPortfolio} className="glass rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-foreground/5 transition-colors duration-200">
          <Plus size={15} weight="bold" /> Watch
        </button>
      </div>

      {/* Header cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="glass rounded-3xl p-5">
          <div className="text-xs text-muted-foreground mb-1">Trade</div>
          <div className={`font-display text-3xl font-black ${actionStyle(d.action).split(" ")[0]}`} data-testid="detail-action">{d.action}</div>
          <div className="text-xs text-muted-foreground mt-1">{d.ta_available ? `Bratz: ${d.ta_decision}` : "Group call (no data)"}</div>
        </div>
        <div className="glass rounded-3xl p-5">
          <div className="text-xs text-muted-foreground mb-1">TA Score</div>
          <div className="font-mono text-3xl font-bold">{d.signal_score}<span className="text-muted-foreground text-lg">/100</span></div>
          <div className={`text-xs mt-1 ${stateColor(d.direction)}`}>{d.direction} · norm {fmt(d.norm_score, 1)}</div>
        </div>
        <div className="glass rounded-3xl p-5">
          <div className="text-xs text-muted-foreground mb-1">Conviction</div>
          <div className={`font-mono text-3xl font-bold ${confColor(d.confluence_score)}`}>{d.confluence_score}</div>
          <div className="text-xs text-muted-foreground mt-1">{d.num_sources} sources · {d.group_bias}</div>
        </div>
        <div className="glass rounded-3xl p-5">
          <div className="text-xs text-muted-foreground mb-2">Crowd Sentiment</div>
          <SentimentMeter sentiment={d.sentiment} size="sm" />
        </div>
      </div>

      {!d.ta_available && (
        <div className="flex items-center gap-2 glass rounded-2xl text-neutral text-sm px-4 py-3 mb-4">
          <Warning size={16} weight="fill" /> Live market data unavailable — technical strategies not computed. Levels are from the source call.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          {d.strategies?.length > 0 && (
            <div className="glass-strong rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-border font-display font-bold">Strategy Matrix</div>
              <div className="divide-y divide-border/50">
                {d.strategies.map((s) => (
                  <div key={s.code} data-testid={`strategy-${s.code}`} className="flex items-center gap-4 px-6 py-2.5">
                    <span className="flex-1 text-sm text-muted-foreground">{s.label}</span>
                    <span className={`text-xs font-mono ${stateColor(s.state)}`}>{s.triggered ? "● TRIGGERED" : "— neutral"}</span>
                    <div className="flex items-center gap-2 w-28 justify-end">
                      <div className="w-16 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                        <div className={`h-full rounded-full ${s.score >= s.min_score ? "bg-bull" : "bg-foreground/25"}`} style={{ width: `${Math.min(100, s.score)}%` }} />
                      </div>
                      <span className="text-muted-foreground text-xs font-mono">{s.score}/{s.min_score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {d.signal_cells?.length > 0 && (
            <div className="glass-strong rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-border font-display font-bold">Signal Components</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-border/50">
                {d.signal_cells.map((c) => (
                  <div key={c.name} className="p-4 text-center">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.name}</div>
                    <div className={`font-mono text-xl font-bold mt-1 ${stateColor(c.state)}`}>{c.value > 0 ? `+${c.value}` : c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources */}
          <div className="glass-strong rounded-3xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border font-display font-bold">Source Calls ({d.sources?.length || 0})</div>
            <div className="divide-y divide-border/50">
              {(d.sources || []).map((s) => (
                <div key={s.id} data-testid={`source-${s.id}`} className="p-5 flex flex-col md:flex-row md:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold">{s.group_name}</span>
                      {s.group_win_rate != null && <span className={`text-[10px] font-mono font-semibold ${s.group_win_rate >= 60 ? "text-bull" : s.group_win_rate >= 40 ? "text-neutral" : "text-bear"}`}>{s.group_win_rate}% win</span>}
                      <span className="text-xs text-muted-foreground">· {s.sender}</span>
                      {s.action && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${actionStyle(s.action)}`}>{s.action}</span>}
                      <span className="text-[10px] text-muted-foreground uppercase">{s.conviction}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{fmtTime(s.ts)}</span>
                    </div>
                    <div className="text-sm">"{s.text}"</div>
                    {s.reasoning && <div className="text-xs text-muted-foreground mt-1.5">{s.reasoning}</div>}
                  </div>
                  <div className="flex md:flex-col gap-4 md:gap-1 text-xs font-mono md:text-right md:min-w-[130px]">
                    <span className="text-muted-foreground">E <span className="text-foreground">{fmt(s.entry_price)}</span></span>
                    <span className="text-muted-foreground">SL <span className="text-bear">{fmt(s.stop_loss)}</span></span>
                    <span className="text-muted-foreground">T1 <span className="text-bull">{fmt(s.target_1)}</span></span>
                  </div>
                </div>
              ))}
              {(!d.sources || d.sources.length === 0) && <div className="p-8 text-center text-muted-foreground text-sm">No linked source calls.</div>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-strong rounded-3xl overflow-hidden">
            <div className={`px-6 py-4 border-b border-border font-display font-bold ${isShort ? "text-bear" : "text-bull"}`}>Levels · {d.action}</div>
            <div className="divide-y divide-border/50 font-mono text-sm">
              {[["Entry", fmt(lv.entry), "", "text-muted-foreground"],
                ["Stop Loss", fmt(lv.stop_loss), lv.stop_pct != null ? `${fmt(lv.stop_pct, 2)}%` : "", "text-bear"],
                ["Target 1", fmt(lv.tp1), lv.rr1 ? `1:${lv.rr1}` : "", "text-bull"],
                ["Target 2", fmt(lv.tp2), lv.rr2 ? `1:${lv.rr2}` : "", "text-bull"],
                ["Target 3", fmt(lv.tp3), lv.rr3 ? `1:${lv.rr3}` : "", "text-bull"]].map((r) => (
                <div key={r[0]} className="flex items-center px-6 py-3">
                  <span className={`flex-1 text-xs ${r[3]}`}>{r[0]}</span>
                  <span className="text-foreground">{r[1]}</span>
                  <span className="text-muted-foreground text-xs w-16 text-right">{r[2]}</span>
                </div>
              ))}
            </div>
          </div>

          {d.ta_available && (
            <div className="glass-strong rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-border font-display font-bold">Indicators</div>
              <div className="grid grid-cols-2 divide-x divide-y divide-border/50 font-mono text-sm">
                {[["RSI 14", fmt(d.rsi, 1)], ["ATR 14", fmt(d.atr)], ["RVol", fmt(d.rvol)], ["SMA 50", fmt(d.sma50)], ["SMA 200", fmt(d.sma200)], ["Price", fmt(d.price)]].map((m) => (
                  <div key={m[0]} className="px-5 py-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{m[0]}</div><div>{m[1]}</div></div>
                ))}
              </div>
            </div>
          )}

          <div className="glass-strong rounded-3xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border font-display font-bold">Context Gates</div>
            <div className="divide-y divide-border/50 text-sm">
              {GATE_ROWS.map(([label, key]) => (
                <div key={key} data-testid={`gate-${key}`} className="flex items-center px-6 py-2.5">
                  <span className="flex-1 text-xs text-muted-foreground">{label}</span>
                  <span className={`font-mono text-xs ${key === "sector_rs_score" ? "text-foreground" : gateColor(d.gates?.[key])}`}>{d.gates?.[key] ?? "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
